import React, { useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FaBook, FaPlus, FaCheckCircle, FaExclamationTriangle, FaFile, FaTags, FaLayerGroup, FaUndo } from 'react-icons/fa';
import * as api from '../../services/api';

const AddKnowledge = ({ agentInfo, selectedAgent }) => {
  const [addKnowledgeData, setAddKnowledgeData] = useState({
    knowledge_text: '',
    topic: '',
    memory_type: 'SEMANTIC',
    scope: 'agent',
    source_priority: 50
  });
  const [addingKnowledge, setAddingKnowledge] = useState(false);
  const [addKnowledgeResult, setAddKnowledgeResult] = useState(null);
  const [addKnowledgeError, setAddKnowledgeError] = useState(null);

  const handleChange = (field, value) => {
    setAddKnowledgeData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setAddKnowledgeData({
      knowledge_text: '',
      topic: '',
      memory_type: 'SEMANTIC',
      scope: 'agent',
      source_priority: 50
    });
    setAddKnowledgeResult(null);
    setAddKnowledgeError(null);
  };

  const handleAddKnowledge = async (e) => {
    e.preventDefault();
    if (!addKnowledgeData.knowledge_text.trim() || addKnowledgeData.knowledge_text.length < 10) {
      setAddKnowledgeError('Knowledge text must be at least 10 characters long.');
      return;
    }

    if (!agentInfo?.agent701) {
      setAddKnowledgeError('Agent 701 (Add Knowledge) information not available.');
      return;
    }

    setAddingKnowledge(true);
    setAddKnowledgeError(null);
    setAddKnowledgeResult(null);

    try {
      const payload = {
        knowledge_text: addKnowledgeData.knowledge_text.trim(),
        memory_type: addKnowledgeData.memory_type,
        scope: addKnowledgeData.scope,
        source_priority: parseInt(addKnowledgeData.source_priority)
      };
      if (addKnowledgeData.topic) payload.topic = addKnowledgeData.topic.trim();
      if (selectedAgent && selectedAgent !== 'all') payload.agent_id = parseInt(selectedAgent);

      const response = await api.testAgent(
        agentInfo.parentAgent.uniqueIdentifier || agentInfo.parentAgent.repoName,
        'micro-agent',
        agentInfo.agent701.uniqueIdentifier,
        payload,
        'production'
      );
      
      if (response && response.success !== false) {
        let agentOutput = response.agentOutput || response.response || response.message;
        if (response.body && response.body.agentOutput) agentOutput = response.body.agentOutput;

        setAddKnowledgeResult({
          success: true,
          agentOutput: agentOutput || 'Knowledge added successfully'
        });
        setAddKnowledgeData(prev => ({ ...prev, knowledge_text: '' }));
      } else {
        throw new Error(response?.error?.message || response?.message || 'Failed to add knowledge');
      }
    } catch (error) {
      setAddKnowledgeError(error.message || 'Failed to add knowledge.');
    } finally {
      setAddingKnowledge(false);
    }
  };

  return (
    <Card className="kb-card">
      <Card.Header className="kb-card-header bg-success-gradient text-white py-2">
        <h6 className="mb-0"><FaBook className="me-2" />Add New Knowledge</h6>
      </Card.Header>
      <Card.Body className="kb-card-body" style={{ padding: '24px' }}>
        {addKnowledgeError && <Alert variant="danger" dismissible onClose={() => setAddKnowledgeError(null)} className="py-2 mb-3">{addKnowledgeError}</Alert>}
        {addKnowledgeResult && <Alert variant="success" dismissible onClose={() => setAddKnowledgeResult(null)} className="py-2 mb-3"><FaCheckCircle className="me-2" />Knowledge added successfully!</Alert>}

        <Form onSubmit={handleAddKnowledge}>
          <Row>
            <Col lg={9}>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                  <FaFile className="me-2 text-primary" /> Content <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={16}
                  placeholder="Enter knowledge content (min 10 chars)...&#10;&#10;You can enter multiple paragraphs here.&#10;&#10;This text area is designed to comfortably handle longer entries with proper spacing and readability."
                  value={addKnowledgeData.knowledge_text}
                  onChange={(e) => handleChange('knowledge_text', e.target.value)}
                  disabled={addingKnowledge}
                  className="kb-large-textarea"
                  style={{
                    width: '100%',
                    minHeight: '400px',
                    maxHeight: '600px',
                    resize: 'vertical',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    padding: '16px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 229, 255, 0.15)',
                    color: 'var(--pure-white)',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}
                  required
                />
                <Form.Text className="text-muted small">
                  Tip: This field supports multi-paragraph entries. Press Enter to create new paragraphs.
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col lg={3}>
              <div className="p-3 bg-dark rounded border border-secondary h-100">
                <h6 className="text-light mb-3 pb-2 border-bottom border-secondary small text-uppercase">Settings</h6>
                
                <Form.Group style={{ marginBottom: '16px' }}>
                  <Form.Label className="text-muted small">Topic (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="E.g., API Security"
                    value={addKnowledgeData.topic}
                    onChange={(e) => handleChange('topic', e.target.value)}
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group style={{ marginBottom: '16px' }}>
                  <Form.Label className="text-muted small">Memory Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={addKnowledgeData.memory_type}
                    onChange={(e) => handleChange('memory_type', e.target.value)}
                    className="bg-dark text-light border-secondary"
                  >
                    <option value="SEMANTIC">Semantic (Fact)</option>
                    <option value="EPISODIC">Episodic (Event)</option>
                    <option value="PROCEDURAL">Procedural (How-to)</option>
                  </Form.Select>
                </Form.Group>

                <div className="mt-4 d-grid gap-2">
                  <Button 
                    type="submit" 
                    variant="success"
                    disabled={addingKnowledge || addKnowledgeData.knowledge_text.length < 10}
                    className="shadow-sm"
                  >
                    {addingKnowledge ? <Spinner size="sm" /> : <><FaPlus className="me-2" /> Add Knowledge</>}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={resetForm} disabled={addingKnowledge}>
                    <FaUndo className="me-2" /> Reset
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddKnowledge;